import React, { useState, useEffect } from "react";
import { processSingleOrderApproval } from "../services/orderApprovalService";

const modalOverlayStyle = {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: "rgba(15, 23, 42, 0.8)",
  backdropFilter: "blur(4px)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 2000,
  padding: "20px"
};

const modalContentStyle = {
  background: "#ffffff",
  borderRadius: "20px",
  width: "800px",
  maxWidth: "100%",
  maxHeight: "90vh",
  boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  fontFamily: "'Inter', sans-serif"
};

const headerStyle = {
  padding: "24px 32px",
  borderBottom: "1px solid #e2e8f0",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center"
};

const bodyStyle = {
  padding: "32px",
  flex: 1,
  overflowY: "auto",
};

const statusBadge = (status) => {
  const styles = {
    pending: { bg: "#f1f5f9", text: "#64748b", label: "PENDING" },
    processing: { bg: "#eff6ff", text: "#3b82f6", label: "PROCESSING" },
    success: { bg: "#dcfce7", text: "#166534", label: "SUCCESS" },
    error: { bg: "#fee2e2", text: "#991b1b", label: "FAILED" },
    exists: { bg: "#fef3c7", text: "#92400e", label: "SKIPPED" }
  };
  const s = styles[status] || styles.pending;
  return (
    <span style={{ 
      padding: "4px 10px", 
      borderRadius: "6px", 
      fontSize: "11px", 
      fontWeight: 800, 
      background: s.bg, 
      color: s.text,
      textTransform: "uppercase"
    }}>
      {s.label}
    </span>
  );
};

function BatchApprovalModal({ orders, onClose }) {
  const [results, setResults] = useState(
    orders.map(o => ({ id: o.id, name: o.partyName, status: "pending", error: null, step: "" }))
  );
  const [currentIndex, setCurrentIndex] = useState(-1);
  const [isProcessing, setIsProcessing] = useState(false);
  const [overallProgress, setOverallProgress] = useState(0);

  const startBatch = async () => {
    if (isProcessing) return;
    setIsProcessing(true);
    
    for (let i = 0; i < orders.length; i++) {
      setCurrentIndex(i);
      const order = orders[i];
      
      updateStatus(order.id, { status: "processing", step: "Starting..." });
      
      const result = await processSingleOrderApproval(order.id, {
        onStep: (step) => updateStatus(order.id, { step }),
        onProgress: (p) => {} // Internal progress not shown for brevity in batch table
      });

      if (result.success) {
        if (result.status === "ALREADY_APPROVED") {
          updateStatus(order.id, { status: "exists", step: "Already approved" });
        } else {
          updateStatus(order.id, { status: "success", step: `Approved: ${result.orderId}` });
        }
      } else {
        updateStatus(order.id, { status: "error", error: result.error, step: "Failed" });
      }
      
      setOverallProgress(Math.round(((i + 1) / orders.length) * 100));
    }
    
    setIsProcessing(false);
    setCurrentIndex(-1);
  };

  const updateStatus = (id, updates) => {
    setResults(prev => prev.map(r => r.id === id ? { ...r, ...updates } : r));
  };

  const stats = {
    total: orders.length,
    success: results.filter(r => r.status === "success" || r.status === "exists").length,
    error: results.filter(r => r.status === "error").length,
    pending: results.filter(r => r.status === "pending").length
  };

  return (
    <div style={modalOverlayStyle}>
      <div style={modalContentStyle}>
        <div style={headerStyle}>
          <div>
            <h2 style={{ margin: 0, fontSize: "20px", fontWeight: 800, color: "#1e293b" }}>Sequential Batch Approval</h2>
            <p style={{ margin: "4px 0 0 0", fontSize: "14px", color: "#64748b" }}>Processing {orders.length} parties sequentially</p>
          </div>
          {(!isProcessing || overallProgress === 100) && (
            <button 
              onClick={onClose}
              style={{ padding: "8px 16px", background: "#f1f5f9", border: "none", borderRadius: "8px", fontWeight: 700, cursor: "pointer", color: "#475569" }}
            >
              Close
            </button>
          )}
        </div>

        <div style={bodyStyle}>
          {/* Progress Section */}
          <div style={{ marginBottom: "32px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px", fontSize: "14px", fontWeight: 700 }}>
              <span style={{ color: "#1e293b" }}>Overall Progress</span>
              <span style={{ color: "#4f46e5" }}>{overallProgress}%</span>
            </div>
            <div style={{ height: "12px", background: "#f1f5f9", borderRadius: "6px", overflow: "hidden" }}>
              <div style={{ width: `${overallProgress}%`, height: "100%", background: "linear-gradient(90deg, #4f46e5, #818cf8)", transition: "width 0.3s ease" }}></div>
            </div>
            
            <div style={{ display: "flex", gap: "20px", marginTop: "16px" }}>
              <div style={{ flex: 1, background: "#f8fafc", padding: "12px", borderRadius: "12px", border: "1px solid #e2e8f0" }}>
                <div style={{ fontSize: "10px", fontWeight: 800, color: "#94a3b8", textTransform: "uppercase" }}>Success</div>
                <div style={{ fontSize: "18px", fontWeight: 800, color: "#059669" }}>{stats.success}</div>
              </div>
              <div style={{ flex: 1, background: "#f8fafc", padding: "12px", borderRadius: "12px", border: "1px solid #e2e8f0" }}>
                <div style={{ fontSize: "10px", fontWeight: 800, color: "#94a3b8", textTransform: "uppercase" }}>Failures</div>
                <div style={{ fontSize: "18px", fontWeight: 800, color: "#dc2626" }}>{stats.error}</div>
              </div>
              <div style={{ flex: 1, background: "#f8fafc", padding: "12px", borderRadius: "12px", border: "1px solid #e2e8f0" }}>
                <div style={{ fontSize: "10px", fontWeight: 800, color: "#94a3b8", textTransform: "uppercase" }}>Remaining</div>
                <div style={{ fontSize: "18px", fontWeight: 800, color: "#475569" }}>{stats.pending}</div>
              </div>
            </div>
          </div>

          {!isProcessing && overallProgress === 0 && (
            <div style={{ textAlign: "center", padding: "40px", background: "#f8fafc", borderRadius: "16px", border: "2px dashed #e2e8f0" }}>
               <h3 style={{ margin: 0, color: "#1e293b" }}>Ready to Start</h3>
               <p style={{ color: "#64748b", marginBottom: "24px" }}>The system will process each order one-by-one to ensure stock and ledger accuracy.</p>
               <button 
                onClick={startBatch}
                style={{ padding: "14px 40px", background: "#4f46e5", color: "#fff", border: "none", borderRadius: "12px", fontWeight: 800, cursor: "pointer", boxShadow: "0 10px 15px -3px rgba(79, 70, 229, 0.4)" }}
               >
                 Start Approval Process
               </button>
            </div>
          )}

          {/* Status Table */}
          <table style={{ width: "100%", borderCollapse: "collapse", marginTop: "16px" }}>
            <thead>
              <tr style={{ textAlign: "left", fontSize: "11px", fontWeight: 800, color: "#94a3b8", textTransform: "uppercase", borderBottom: "1px solid #f1f5f9" }}>
                <th style={{ padding: "12px 0" }}>Party Name</th>
                <th style={{ padding: "12px 0" }}>Status</th>
                <th style={{ padding: "12px 0" }}>Last Action / Error</th>
              </tr>
            </thead>
            <tbody>
              {results.map((r, i) => (
                <tr key={r.id} style={{ borderBottom: "1px solid #f8fafc", background: currentIndex === i ? "#f0f9ff" : "transparent" }}>
                  <td style={{ padding: "16px 0", fontWeight: 600, color: "#1e293b", fontSize: "14px" }}>{r.name}</td>
                  <td style={{ padding: "16px 0" }}>{statusBadge(r.status)}</td>
                  <td style={{ padding: "16px 0", fontSize: "13px", color: r.status === "error" ? "#dc2626" : "#64748b" }}>
                    {r.error || r.step}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {isProcessing && (
          <div style={{ padding: "16px 32px", background: "#fffbeb", borderTop: "1px solid #fef3c7" }}>
             <p style={{ margin: 0, fontSize: "12px", color: "#92400e", fontWeight: 600, display: "flex", alignItems: "center", gap: "8px" }}>
               <span style={{ display: "inline-block", width: "8px", height: "8px", background: "#f59e0b", borderRadius: "50%" }}></span>
               Do not close the browser while approval is in progress.
             </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default BatchApprovalModal;
